import { useCallback, useEffect, useRef, useState } from 'react';

import { readRedHerringQuestions } from '@api/instant_survey';
import type { Question } from '@api/instant_survey/types';
import { Button, ButtonV2, LoaderSkeleton, ModalV2 as Modal } from '@utilities';

import Results from './components/Results';

import styles from './_index.module.scss';

import { getQuestionsCost } from '../../../../utilities/helpers';

const MODAL_TITLE = 'Add Attention Check';
const QUESTION_LIMIT = 30;

interface ModalAttentionCheckProps {
  maxQuestions: number;
  onClose: () => void;
  onSelectQuestions: (questions: Question[]) => Promise<void>;
  selectedQuestions: Question[];
}

const ModalAttentionCheck = ({
  maxQuestions,
  onClose,
  onSelectQuestions,
  selectedQuestions,
}: ModalAttentionCheckProps) => {
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isAddingToSurvey, setIsAddingToSurvey] = useState(false);
  const requestControllerRef = useRef<AbortController | null>(null);

  const fetchQuestions = useCallback(async () => {
    requestControllerRef.current?.abort();
    const controller = new AbortController();
    requestControllerRef.current = controller;

    setIsLoading(true);
    setIsError(false);

    try {
      const { results } = await readRedHerringQuestions({
        limit: QUESTION_LIMIT,
        signal: controller.signal,
      });

      setQuestions(results);
    } catch {
      if (!controller.signal.aborted) setIsError(true);
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchQuestions();

    return () => requestControllerRef.current?.abort();
  }, [fetchQuestions]);

  const toggleQuestion = (id: number) => {
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAddToSurvey = async () => {
    if (!selectedIds.size || isAddingToSurvey) return;

    const selectedQuestions = questions.filter((question) => selectedIds.has(question.id));

    setIsAddingToSurvey(true);
    try {
      await onSelectQuestions(selectedQuestions);
    } finally {
      setIsAddingToSurvey(false);
    }
  };

  const actionButtons = [
    <Button
      isDisabled={isAddingToSurvey}
      key="modal-cancel-button"
      onClick={onClose}
      text="Cancel"
      variant="secondary"
    />,
    <Button
      isDisabled={!selectedIds.size || isAddingToSurvey}
      isLoading={isAddingToSurvey}
      key="modal-confirm-button"
      onClick={handleAddToSurvey}
      text="Add to Survey"
    />,
  ];

  const availableSlots = maxQuestions - getQuestionsCost(selectedQuestions);
  const selectedCost = getQuestionsCost(
    questions.filter((question) => selectedIds.has(question.id))
  );
  const remainingSlots = availableSlots - selectedCost;

  return (
    <Modal
      buttons={actionButtons}
      className={styles['modal-attention-check']}
      isActive
      isScrollable
      onClose={onClose}
      size="large"
      title={MODAL_TITLE}
    >
      <p className={styles['modal-attention-check-intro']}>
        Attention checks help verify that respondents are reading carefully. Select the questions
        you want to add to your survey.
      </p>

      {isLoading && (
        <LoaderSkeleton>
          <rect height="15" rx="2" ry="2" width="200" x="0" y="0" />
          <rect height="15" rx="2" ry="2" width="175" x="0" y="25" />
          <rect height="15" rx="2" ry="2" width="225" x="0" y="50" />
          <rect height="15" rx="2" ry="2" width="200" x="0" y="75" />
        </LoaderSkeleton>
      )}

      {!isLoading && isError && (
        <p className={styles['modal-attention-check-message']}>
          Attention check questions could not be loaded.{' '}
          <ButtonV2 onClick={fetchQuestions} size="small" type="button" variant="default">
            Try again.
          </ButtonV2>
        </p>
      )}

      {!isLoading && !isError && questions.length === 0 && (
        <p className={styles['modal-attention-check-message']}>
          There are no attention check questions available yet.
        </p>
      )}

      {!isLoading && !isError && questions.length > 0 && (
        <Results
          onToggle={toggleQuestion}
          questions={questions}
          remainingSlots={remainingSlots}
          selectedIds={selectedIds}
          selectedQuestions={selectedQuestions}
        />
      )}
    </Modal>
  );
};

export default ModalAttentionCheck;
